import { Injectable, inject } from '@angular/core';
import { ApiService } from 'capiche-api';
import { UserService } from 'capiche-auth';
import { CapicheLoggerService } from 'capiche-logger';
import { CAPICHE_SUBSCRIPTION_CONFIG } from '../capiche-subscription.config';
import { ProductViewModel } from '../models/product-view.model';

@Injectable({
  providedIn: 'root',
})
export class StripeApiService extends ApiService {
  private readonly config = inject(CAPICHE_SUBSCRIPTION_CONFIG);
  override logger: CapicheLoggerService = new CapicheLoggerService('stripe-api.service:', false);

  private stripeIntegrationApiBaseUrl = this.config.baseStripeApiUrl;

  private stripeCheckoutEndpoint = `${this.stripeIntegrationApiBaseUrl}/v1/checkout`;
  private stripeProductFromPriceIdEndpoint = `${this.stripeIntegrationApiBaseUrl}/v1/product`;
  private stripeSubscriptionStatusEndpoint = `${this.stripeIntegrationApiBaseUrl}/v1/subscriptionStatus`;
  private stripeCustomerPortalEndpoint = `${this.stripeIntegrationApiBaseUrl}/v1/customerPortal`;

  constructor(userService: UserService) {
    super(userService);
    this.logger.log('constructor', userService.currentUser$);
  }

  /**
   * Get the user stripe id
   */
  public async getCheckoutUrl(input: { uid: string; priceId: string }): Promise<string | null> {
    this.logger.log('getCheckoutUrl: input:', input);
    return new Promise<string | null>((resolve, reject) => {
      this.post(`${this.stripeCheckoutEndpoint}`, input)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          this.logger.log('getCheckoutUrl: response.body:', response.body);
          resolve(response.body as string);
        })
        .catch((error) => {
          this.logger.error('getCheckoutUrl: error:', error);
          resolve(null); // Resolve the Promise with null in case of an error
        });
    });
  }

  /**
   * Get the product data
   */
  public async getProductFromPriceId(price_id: string): Promise<ProductViewModel | null> {
    this.logger.log('getProductFromPriceId: priceId:', price_id);
    return new Promise<ProductViewModel | null>((resolve, reject) => {
      this.get(`${this.stripeProductFromPriceIdEndpoint}/${price_id}`)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          this.logger.log('getProductFromPriceId: response.body:', response.body);
          resolve(response.body as ProductViewModel);
        })
        .catch((error) => {
          this.logger.error('getProductFromPriceId: error:', error);
          resolve(null); // Resolve the Promise with null in case of an error
        });
    });
  }

  /**
   * Get the user subscription status
   */
  public async getSubscriptionStatus(): Promise<boolean> {
    this.logger.log('getSubscriptionStatus: start');
    const maxRetries = 5;
    let retries = 0;
    const delayTime = 1000;
    while (retries < maxRetries && !this.currentUser) {
      retries++;
      this.logger.log('getSubscriptionStatus: waiting for currentUser:', retries);
      await new Promise((resolve) => setTimeout(resolve, delayTime * retries));
    }
    return new Promise<boolean>((resolve, reject) => {
      this.get(`${this.stripeSubscriptionStatusEndpoint}/${this.currentUser?.uid ?? ''}`)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          this.logger.log('getSubscriptionStatus: response.body:', response.body);
          resolve(response.body as boolean);
        })
        .catch((error) => {
          this.logger.error('getSubscriptionStatus: error:', error);
          resolve(false); // Resolve the Promise with null in case of an error
        });
    });
  }

  /**
   * Get the customer portal url
   */
  public async getCustomerPortalUrl(): Promise<string> {
    this.logger.log('getCustomerPortalUrl: start');
    const maxRetries = 5;
    let retries = 0;
    const delayTime = 1000;
    while (retries < maxRetries && !this.currentUser) {
      retries++;
      this.logger.log('getCustomerPortalUrl: waiting for currentUser:', retries);
      await new Promise((resolve) => setTimeout(resolve, delayTime * retries));
    }
    return new Promise<string>((resolve, reject) => {
      this.get(`${this.stripeCustomerPortalEndpoint}/${this.currentUser?.uid ?? ''}`)
        .then((response) => {
          if (response == null) {
            throw new Error('Invalid response returned by endpoint');
          }
          this.logger.log('getCustomerPortalUrl: response.body:', response.body);
          resolve(response.body as string);
        })
        .catch((error) => {
          this.logger.error('getCustomerPortalUrl: error:', error);
          resolve(''); // Resolve the Promise with null in case of an error
        });
    });
  }
}
